import { useContext, useMemo } from "react";

import { FormType } from "@simplyk/common";
import { useWatch } from "react-hook-form";

import { scrollToTop } from "../../../../../../../common/helpers/utils";
import { useFormV2Context } from "../../../../contexts/FormV2Context";
import { findStep1FirstErrorPath } from "../../../../helpers/findFirstErrorPath";
import { getBuyerPrefillFromParticipants } from "../../../../helpers/getBuyerPrefillFromParticipants";
import { splitCartQuantities } from "../../../../helpers/splitCartQuantities";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { FrontendFormContext } from "@/common/contexts/FrontendFormContext";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";

export const useTicketingFirstStepSubmit = () => {
  const { logAmplitudeEvent } = useAmplitude();
  const {
    form,
    setCurrentStep,
    formObject: ticketing,
    setValidationMode,
    questions,
    hasAddOnsStep,
    addOnRates,
    memberInfo,
  } = useFormV2Context(FormType.Ticketing);
  const { formAmplitudeContext } = useContext(FrontendFormContext);

  const { trigger, control, getValues, setValue } = form;

  const ticketsPurchased = useWatch({
    control,
    name: "ticketsPurchased",
  });
  const addOnRateIds = useMemo(() => new Set(addOnRates.map((rate) => rate.id)), [addOnRates]);
  const { itemsCount: totalTickets, addOnsCount: totalAddOns } = splitCartQuantities(ticketsPurchased, addOnRateIds);

  const logDonorFormStepSubmitted = () => {
    logAmplitudeEvent(AmplitudeEvents.DonorFormStepSubmitted, {
      formId: ticketing.id,
      formType: ticketing.formCategory,
      organizationCountry: ticketing.organization?.country,
      location: "memorable-form.step-1",
      step: "main",
      items_count: totalTickets,
      addons_count: totalAddOns,
      ...formAmplitudeContext,
    });
  };

  const additionalDonation = useWatch({
    control,
    name: "extraDonation",
  });

  const hasPurchaseOrDonation: boolean = totalTickets > 0 || Boolean(additionalDonation);
  const canSubmitFirstStep = hasPurchaseOrDonation;

  const focusWithScroll = (element: HTMLElement) => {
    if (element) {
      const elementRect = element.getBoundingClientRect();
      const isElementBelowHalfScreen = elementRect.bottom > window.innerHeight / 2;
      element.focus();
      if (isElementBelowHalfScreen) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  const handleErrorFocus = () => {
    const errors = form.formState.errors;
    const firstErrorFieldName = findStep1FirstErrorPath(errors);
    const firstErrorElement = firstErrorFieldName ? document.getElementsByName(firstErrorFieldName)[0] : null;

    if (firstErrorElement) {
      focusWithScroll(firstErrorElement);
    } else {
      form.trigger();
    }
  };

  const prefillBuyerFromFirstParticipant = () => {
    if (memberInfo.isEnabled) {
      return;
    }

    const { email, firstName, lastName } = getBuyerPrefillFromParticipants(
      getValues("customAnswersOfParticipants"),
      questions
    );

    if (email && !getValues("email")) {
      setValue("email", email);
    }
    if (firstName && !getValues("firstName")) {
      setValue("firstName", firstName);
    }
    if (lastName && !getValues("lastName")) {
      setValue("lastName", lastName);
    }
  };

  const onFirstStepSubmit = async () => {
    if (!canSubmitFirstStep) {
      return;
    }

    const isValid = await trigger(
      [
        "ticketsPurchased",
        "customAnswersOfParticipants",
        "extraDonation",
        "manualExtraDonation",
        "addGiftAid",
        "hasGiftAidConfirmation",
      ],
      { shouldFocus: true }
    );

    if (isValid) {
      prefillBuyerFromFirstParticipant();
      scrollToTop("auto");
      setCurrentStep(hasAddOnsStep ? "addOns" : "payment");
      logDonorFormStepSubmitted();
      setValidationMode("onBlur");
    } else {
      handleErrorFocus();
      setValidationMode("onChange");
    }
  };

  return { onFirstStepSubmit, canSubmitFirstStep, totalTickets, additionalDonation: additionalDonation || 0 };
};
